import { X } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useLabels } from '../config/useLabels';

export interface CampUnpaidInvoiceItem {
  invoiceId: string;
  programName: string;
  studentName?: string;
  amountDue: number;
}

interface CampPaymentNudgeProps {
  invoices: CampUnpaidInvoiceItem[];
  onDismiss?: () => void;
  variant?: 'modal' | 'banner';
}

/**
 * Registration is complete only once payment is received (SUMMER_CAMP_POLICY.registrationNotice),
 * which is what this says. It no longer says the spot is unreserved until then: an enrollment holds
 * its seat from the moment it is made, paid or not, and a capped program counts it (BHCS-124).
 */
export function CampPaymentNudge({ invoices, onDismiss, variant = 'modal' }: CampPaymentNudgeProps) {
  const t = useLabels();
  const normalizedInvoices = (Array.isArray(invoices) ? invoices : []).filter(
    (invoice): invoice is CampUnpaidInvoiceItem =>
      typeof invoice.invoiceId === 'string' &&
      typeof invoice.programName === 'string' &&
      typeof invoice.amountDue === 'number' &&
      Number.isFinite(invoice.amountDue)
  );

  if (normalizedInvoices.length === 0) {
    return null;
  }

  const hasMultipleInvoices = normalizedInvoices.length > 1;
  const isBanner = variant === 'banner';

  if (isBanner) {
    return (
      <div
        className="rounded-lg border border-amber-300 bg-amber-50 p-4 flex flex-col sm:flex-row sm:items-start gap-3"
        role="alert"
        data-testid="camp-nudge-banner"
      >
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-amber-800">
            Enrollment not complete - payment required
          </p>
          <p className="text-sm text-amber-700 mt-0.5">
            {hasMultipleInvoices ? (
              <>
                You still have <span className="font-medium">{normalizedInvoices.length} unpaid invoices</span>.
                Each enrollment is complete once its payment is received.
              </>
            ) : (
              <>
                Your enrollment request for <span className="font-medium">{normalizedInvoices[0].programName}</span>
                {normalizedInvoices[0].studentName && (
                  <> for <span className="font-medium">{normalizedInvoices[0].studentName}</span></>
                )}{' '}
                is received. It is complete once payment is received.
              </>
            )}
          </p>
        </div>
        <div className="flex-shrink-0">
          {hasMultipleInvoices ? (
            <Link
              to="/payments"
              className="block text-center px-4 py-1.5 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
              data-testid="camp-banner-pay-now"
              onClick={onDismiss}
            >
              {t('action.payNow')}
            </Link>
          ) : (
            <Link
              to={`/payment-checkout/${normalizedInvoices[0].invoiceId}`}
              className="block text-center px-4 py-1.5 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
              data-testid="camp-banner-pay-now"
              onClick={onDismiss}
            >
              {t('action.payNow')}
            </Link>
          )}
        </div>
      </div>
    );
  }

  return (
    <div
      className="fixed inset-0 z-[60] flex flex-col justify-end sm:items-center sm:justify-center"
      role="dialog"
      aria-modal="true"
      aria-labelledby="camp-nudge-title"
    >
      <div className="absolute inset-0 bg-black/50" aria-hidden="true" />
      <div className="relative w-full bg-white rounded-t-2xl shadow-2xl sm:rounded-xl sm:max-w-md sm:my-8 flex flex-col max-h-[90vh]">
        <div className="flex justify-center pt-3 pb-1 sm:hidden" aria-hidden="true">
          <div className="w-10 h-1 rounded-full bg-gray-300" />
        </div>
        <div className="flex items-center justify-between px-5 py-3 border-b border-gray-200">
          <h3 id="camp-nudge-title" className="text-base font-semibold text-gray-900">
            Action Required: Complete Payment for Your Enrollment
          </h3>
          <button
            onClick={onDismiss}
            className="p-1 rounded-full text-gray-400 hover:text-gray-600 hover:bg-gray-100"
            aria-label="Dismiss payment reminder"
            data-testid="camp-nudge-close-x"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="overflow-y-auto p-5" style={{ paddingBottom: "max(1.25rem, env(safe-area-inset-bottom, 0px))" }}>
          <p className="text-sm text-gray-600 mb-4">
            {hasMultipleInvoices ? (
              <>
                You have <span className="font-medium">{normalizedInvoices.length} unpaid invoices</span>. Your
                enrollment requests are received, and each is complete once its payment is received. Please
                complete payment to finish enrolling.
              </>
            ) : (
              <>
                Your enrollment request for <span className="font-medium">{normalizedInvoices[0].programName}</span>
                {normalizedInvoices[0].studentName && (
                  <> for <span className="font-medium">{normalizedInvoices[0].studentName}</span></>
                )}{' '}
                is received. It is complete once payment is received, so please complete payment to finish
                enrolling.
              </>
            )}
          </p>
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-5 space-y-2">
            {normalizedInvoices.map((invoice, index) => (
              <div key={invoice.invoiceId} className="flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <p className="text-sm text-amber-900 truncate">{invoice.programName}</p>
                  {invoice.studentName && (
                    <p className="text-xs text-amber-700 truncate">{invoice.studentName}</p>
                  )}
                  <p className="text-xs text-amber-800">
                    Amount due: <span className="font-semibold">${invoice.amountDue.toFixed(2)}</span>
                  </p>
                </div>
                <Link
                  to={`/payment-checkout/${invoice.invoiceId}`}
                  className="px-3 py-1.5 bg-blue-600 text-white text-xs sm:text-sm font-medium rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 whitespace-nowrap"
                  data-testid={index === 0 ? 'camp-nudge-pay-now' : `camp-nudge-pay-now-${invoice.invoiceId}`}
                  onClick={onDismiss}
                >
                  {t('action.payNow')}
                </Link>
              </div>
            ))}
          </div>
          <div className="flex items-center gap-3 justify-end">
            <button
              onClick={onDismiss}
              className="px-4 py-2 text-sm font-medium text-gray-600 hover:text-gray-800 rounded-lg hover:bg-gray-100"
              data-testid="camp-nudge-close-btn"
            >
              Dismiss
            </button>
          </div>
        </div>{/* overflow-y-auto body */}
      </div>
    </div>
  );
}
